import React, { useState, useEffect } from 'react';
import axios from 'axios';
import baseURL, { getAuthHeaders, handleAuthError } from './baseUrl';
import './UserManagement.css';

const MODULES = [
  { key: 'buy', label: 'Buy' },
  { key: 'sell', label: 'Sell' },
  { key: 'add', label: 'Add' },
  { key: 'stock', label: 'Stock' },
  { key: 'clients', label: 'Clients' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'convert', label: 'Convert' },
  { key: 'speed', label: 'Speed' },
  { key: 'speedThane', label: 'Speed Thane' },
  { key: 'articles', label: 'Articles' },
  { key: 'advance', label: 'Advance' },
];

const ACTIONS = ['view', 'create', 'edit', 'delete'];

const getDefaultPermissions = () => {
  const perms = {};
  MODULES.forEach((m) => {
    perms[m.key] = { view: false, create: false, edit: false, delete: false };
  });
  return perms;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    permissions: getDefaultPermissions()
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${baseURL}/api/auth/users`, {
        headers: getAuthHeaders()
      });
      setUsers(res.data.users);
    } catch (err) {
      handleAuthError(err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setFormData({ name: '', password: '', permissions: getDefaultPermissions() });
    setEditingUser(null);
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  const handlePermissionChange = (moduleKey, action) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          ...prev.permissions[moduleKey],
          [action]: !prev.permissions[moduleKey][action]
        }
      }
    }));
  };

  const handleSelectAll = (moduleKey) => {
    const allChecked = ACTIONS.every((a) => formData.permissions[moduleKey][a]);
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [moduleKey]: {
          view: !allChecked,
          create: !allChecked,
          edit: !allChecked,
          delete: !allChecked
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        const payload = { name: formData.name, permissions: formData.permissions };
        if (formData.password) payload.password = formData.password;

        await axios.put(`${baseURL}/api/auth/users/${editingUser._id}`, payload, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
        });
        setSuccess('User updated successfully');
      } else {
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        await axios.post(`${baseURL}/api/auth/users`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }
        });
        setSuccess('User created successfully');
      }
      fetchUsers();
      setTimeout(() => resetForm(), 1000);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      password: '',
      permissions: { ...getDefaultPermissions(), ...user.permissions }
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${baseURL}/api/auth/users/${userId}`, {
        headers: getAuthHeaders()
      });
      setSuccess('User deleted');
      fetchUsers();
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(
        `${baseURL}/api/auth/users/${user._id}`,
        { isActive: !user.isActive },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      fetchUsers();
    } catch (err) {
      handleAuthError(err);
    }
  };

  const permissionLabels = (user) => {
    if (user.role === 'superadmin') return ['All access'];
    return MODULES.filter((m) => user.permissions?.[m.key]?.view).map((m) => m.label);
  };

  if (loading) {
    return (
      <div className="um-page">
        <div className="um-loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="um-page">
      <div className="um-header">
        <div>
          <h1 className="um-title">User Management</h1>
          <p className="um-subtitle">Create users and control module access</p>
        </div>
        {!showForm && (
          <button
            type="button"
            className="um-btn um-btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Create User
          </button>
        )}
      </div>

      {error && <div className="um-alert um-alert-error">{error}</div>}
      {success && <div className="um-alert um-alert-success">{success}</div>}

      {showForm && (
        <section className="um-panel um-form-panel">
          <div className="um-panel-head">
            <h2>{editingUser ? `Edit · ${editingUser.name}` : 'Create New User'}</h2>
            <button type="button" className="um-btn um-btn-ghost" onClick={resetForm}>
              Close
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="um-form-grid">
              <label className="um-field">
                <span>Name</span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  placeholder="Staff name"
                />
              </label>
              <label className="um-field">
                <span>
                  Password
                  {editingUser ? <em> · leave empty to keep current</em> : null}
                </span>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
                  required={!editingUser}
                  minLength={4}
                  placeholder={editingUser ? 'Leave empty to keep current' : 'Enter password'}
                />
              </label>
            </div>

            <div className="um-perm-block">
              <div className="um-perm-title">
                <h3>Module Permissions</h3>
                <p>Tick what this user can access</p>
              </div>

              <div className="um-perm-table-wrap">
                <table className="um-perm-table">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>All</th>
                      {ACTIONS.map((a) => (
                        <th key={a}>{a}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULES.map((mod) => {
                      const allOn = ACTIONS.every((a) => formData.permissions[mod.key]?.[a]);
                      return (
                        <tr key={mod.key}>
                          <td className="um-module-name">{mod.label}</td>
                          <td>
                            <label className="um-check">
                              <input
                                type="checkbox"
                                checked={allOn}
                                onChange={() => handleSelectAll(mod.key)}
                              />
                              <span />
                            </label>
                          </td>
                          {ACTIONS.map((action) => (
                            <td key={action}>
                              <label className="um-check">
                                <input
                                  type="checkbox"
                                  checked={formData.permissions[mod.key]?.[action] || false}
                                  onChange={() => handlePermissionChange(mod.key, action)}
                                />
                                <span />
                              </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="um-form-actions">
              <button type="submit" className="um-btn um-btn-primary">
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="um-btn um-btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="um-panel">
        <div className="um-panel-head">
          <h2>Users ({users.length})</h2>
        </div>

        {users.length === 0 ? (
          <div className="um-empty">No users yet. Create one to get started.</div>
        ) : (
          <div className="um-user-list">
            {users.map((user) => {
              const labels = permissionLabels(user);
              return (
                <article key={user._id} className="um-user-card">
                  <div className="um-user-main">
                    <div className="um-avatar">{(user.name || '?').charAt(0).toUpperCase()}</div>
                    <div className="um-user-meta">
                      <div className="um-user-name-row">
                        <h3>{user.name}</h3>
                        <span className={`um-badge ${user.role === 'superadmin' ? 'um-badge-admin' : 'um-badge-user'}`}>
                          {user.role}
                        </span>
                        <span className={`um-badge ${user.isActive ? 'um-badge-active' : 'um-badge-inactive'}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="um-perm-chips">
                        {labels.length === 0 ? (
                          <span className="um-chip um-chip-muted">No permissions</span>
                        ) : (
                          labels.map((label) => (
                            <span key={label} className="um-chip">
                              {label}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {user.role !== 'superadmin' && (
                    <div className="um-user-actions">
                      <button type="button" className="um-btn um-btn-sm um-btn-secondary" onClick={() => handleEdit(user)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`um-btn um-btn-sm ${user.isActive ? 'um-btn-ghost' : 'um-btn-success'}`}
                        onClick={() => handleToggleActive(user)}
                      >
                        {user.isActive ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        className="um-btn um-btn-sm um-btn-danger"
                        onClick={() => handleDelete(user._id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default UserManagement;
